import _ from 'lodash';
import {connect} from 'react-redux';

import {isDefined} from '../../../../../libs/shared/core/src/helpers/typescript/typeHelpers';
import {createSelector, makeMapStateToProps} from '../../../../../srcCommon/helpers/reselectHelpers';
import {buildAccountCrmExternalUrl} from '../../../../helpers/accountHelpers';
import {AccountModel} from '../../../../models/api/accountModel';
import {CrmSyncIntegrationResourceTypesEnum} from '../../../../models/api/crmSyncModel';
import {AppState} from '../../../../store/appState';
import {getFlatCrmSyncs} from '../../../../store/data/crmSync/crmSyncsSelectors';
import {getCustomFieldsForAccounts} from '../../../../store/data/customFields/customFieldsSelectors';
import {AccountCustomAttributes} from './accountCustomAttributes';

/*
 * Props.
 */

interface AccountCustomAttributesContainerProps {
  account: AccountModel;
}

/*
 * Selectors.
 */

function getAccount(_state: AppState, props: AccountCustomAttributesContainerProps) {
  return props.account;
}

function makeGetCustomAttributes() {
  return createSelector([getCustomFieldsForAccounts, getAccount], (customFields, account) =>
    (account.customFieldAttributes || [])
      .map((customFieldAttribute) => {
        const customField = customFields[customFieldAttribute.id];

        return customField ? {customField, value: customFieldAttribute.value} : undefined;
      })
      .filter(isDefined),
  );
}

function makeGetCrmSync() {
  return createSelector([getAccount, getFlatCrmSyncs], (account, crmSyncs) => {
    // Check if the account has external info attached.
    const {externalInfo} = account;
    if (!externalInfo) {
      return undefined;
    }

    // Try and find the crm sync from the external info.
    const crmSync = crmSyncs.find(
      (c) =>
        c.source === externalInfo.source && c.resourceType === CrmSyncIntegrationResourceTypesEnum.ACCOUNT,
    );
    return crmSync;
  });
}

function makeGetCrmSyncEditLink(getCrmSync: ReturnType<typeof makeGetCrmSync>) {
  return createSelector([getCrmSync, getAccount], (crmSync, account) => {
    if (!crmSync) {
      return undefined;
    }
    return buildAccountCrmExternalUrl(account, crmSync);
  });
}

function makeGetCrmSyncCustomFieldIds(getCrmSync: ReturnType<typeof makeGetCrmSync>) {
  return createSelector([getCrmSync], (crmSync) => {
    if (!crmSync) {
      return [];
    }
    return _(crmSync.mapping)
      .values()
      .filter((id) => id > 0)
      .value();
  });
}

function makeGetCrmSyncStatus(getCrmSync: ReturnType<typeof makeGetCrmSync>) {
  return createSelector([getCrmSync], (crmSync) => {
    if (!crmSync) {
      return undefined;
    }

    return crmSync.status;
  });
}

/*
 * Redux.
 */

const mapStateToProps = makeMapStateToProps<AccountCustomAttributesContainerProps>()(() => {
  const getCustomAttributes = makeGetCustomAttributes();
  const getCrmSync = makeGetCrmSync();
  return {
    account: getAccount,
    customAttributes: getCustomAttributes,
    crmSyncEditLink: makeGetCrmSyncEditLink(getCrmSync),
    crmSyncCustomFieldIds: makeGetCrmSyncCustomFieldIds(getCrmSync),
    crmSyncStatus: makeGetCrmSyncStatus(getCrmSync),
  };
});

export const AccountCustomAttributesContainer = connect(mapStateToProps)(AccountCustomAttributes);
